import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import StepHeader from "../../components/projectFlow/StepHeader/StepHeader";
import Step1 from "../../components/projectFlow/Step1/Step1";
import Step2 from "../../components/projectFlow/Step2/Step2";
import Step3 from "../../components/projectFlow/Step3/Step3";
import Step4 from "../../components/projectFlow/Step4/Step4";
import "./projectFlow.scss";

const ProjectFlow = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [projectData, setProjectData] = useState({
        email: sessionStorage.getItem("projectEmail") || "",
        step1: null,
        step2: null,
        step3: null,
        step4: null,
    });

    const handleNext = (stepData) => {
        setProjectData((prev) => ({
            ...prev,
            [`step${currentStep}`]: stepData,
        }));

        if (currentStep === 4) {
            handleSubmit({ ...projectData, step4: stepData });
        } else {
            setCurrentStep(currentStep + 1);
            window.scrollTo(0, 0);
        }
    };

    const handleBack = () => {
        if (currentStep > 1) {
            setCurrentStep(currentStep - 1);
            window.scrollTo(0, 0);
        } else {
            navigate("/#email-capture");
        }
    };

    const typeNames = {
        institutional: "Institucional",
        retail: "Comercial",
        residential: "Residencial",
        office: "Oficinas",
        manufacturing: "Industrial",
        parking: "Estacionamiento",
        transportation: "Transporte",
        entertainment: "Entretenimiento",
        patrimonial: "Patrimonial",
        healthcare: "Salud",
        warehouse: "Almacén",
        others: "Otros",
    };

    const generateEmailFromTemplate = async (templateName, data) => {
        try {
            const response = await fetch(
                `/templates-email/${templateName}.html`
            );
            let html = await response.text();

            if (templateName === "client-confirmation") {
                html = html
                    .replace(/{{clientName}}/g, data.clientName || "")
                    .replace(/{{currentYear}}/g, new Date().getFullYear());
            } else if (templateName === "admin-notification") {
                html = html
                    .replace(/{{email}}/g, data.email || "No proporcionado")
                    .replace(
                        /{{contactName}}/g,
                        data.step1?.contactName || "No proporcionado"
                    )
                    .replace(
                        /{{phone}}/g,
                        data.step1?.phone || "No proporcionado"
                    )
                    .replace(
                        /{{companyName}}/g,
                        data.step1?.companyName || "No proporcionado"
                    )
                    .replace(
                        /{{projectName}}/g,
                        data.step1?.projectName || "No proporcionado"
                    )
                    .replace(
                        /{{location}}/g,
                        data.step1?.location || "No proporcionado"
                    )
                    .replace(
                        /{{projectArea}}/g,
                        data.step1?.projectArea || "No proporcionado"
                    )
                    .replace(
                        /{{scanningPurpose}}/g,
                        data.step1?.scanningPurpose &&
                            data.step1.scanningPurpose.length > 0
                            ? data.step1.scanningPurpose
                                  .map((p) => `<span class="tag">${p}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{scopeOfWork}}/g,
                        data.step1?.scopeOfWork || "No proporcionado"
                    )
                    .replace(
                        /{{buildingTypes}}/g,
                        data.step2?.buildingTypes &&
                            data.step2.buildingTypes.length > 0
                            ? data.step2.buildingTypes
                                  .map(
                                      (type) =>
                                          `<span class="tag">${
                                              typeNames[type] || type
                                          }</span>`
                                  )
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{areasToScan}}/g,
                        data.step3?.areasToScan &&
                            data.step3.areasToScan.length > 0
                            ? data.step3.areasToScan
                                  .map((a) => `<span class="tag">${a}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{disciplines}}/g,
                        data.step3?.disciplines &&
                            data.step3.disciplines.length > 0
                            ? data.step3.disciplines
                                  .map((d) => `<span class="tag">${d}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{scanningDeliverables}}/g,
                        data.step3?.scanningDeliverables &&
                            data.step3.scanningDeliverables.length > 0
                            ? data.step3.scanningDeliverables
                                  .map((d) => `<span class="tag">${d}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{bimDeliverables}}/g,
                        data.step3?.bimDeliverables &&
                            data.step3.bimDeliverables.length > 0
                            ? data.step3.bimDeliverables
                                  .map((d) => `<span class="tag">${d}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{additionalDetails}}/g,
                        data.step3?.additionalDetails || "No proporcionado"
                    )
                    .replace(
                        /{{buildingConditions}}/g,
                        data.step4?.buildingConditions &&
                            data.step4.buildingConditions.length > 0
                            ? data.step4.buildingConditions
                                  .map((c) => `<span class="tag">${c}</span>`)
                                  .join("")
                            : "No especificado"
                    )
                    .replace(
                        /{{accessRequirements}}/g,
                        data.step4?.accessRequirements || "No proporcionado"
                    )
                    .replace(
                        /{{scanningDate}}/g,
                        data.step4?.scanningDate || "No especificado"
                    )
                    .replace(
                        /{{additionalComments}}/g,
                        data.step4?.additionalComments || "No proporcionado"
                    )
                    .replace(/{{currentYear}}/g, new Date().getFullYear());
            }

            return html;
        } catch (error) {
            console.error("Error generando email desde template:", error);
            return null;
        }
    };

    const handleSubmit = async (finalData) => {
        console.log("Proyecto completo:", finalData);

        try {
            // Generar emails desde templates HTML
            const clientEmailHTML = await generateEmailFromTemplate(
                "client-confirmation",
                {
                    clientName: finalData.step1?.contactName,
                    email: finalData.email,
                }
            );

            const adminEmailHTML = await generateEmailFromTemplate(
                "admin-notification",
                finalData
            );

            console.log("Emails generados correctamente");
            console.log("Email Cliente:", clientEmailHTML);
            console.log("Email Admin:", adminEmailHTML);

            // back enviar emails etc
            /*
            await fetch('/api/send-emails', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    clientEmail: {
                        to: finalData.email,
                        subject: 'Confirmación de Solicitud - Nube de Puntos',
                        html: clientEmailHTML
                    },
                    adminEmail: {
                        to: 'admin@grupoatlante.com',
                        subject: `Nueva Solicitud - ${finalData.step1?.projectName || 'Sin nombre'}`,
                        html: adminEmailHTML
                    }
                })
            });
            */

            navigate("/#contact");
        } catch (error) {
            console.error("Error al procesar la solicitud:", error);
        }
    };

    const stepTitles = {
        1: "Información del Proyecto",
        2: "Tipo de Proyecto",
        3: "Detalles del Alcance",
        4: "Accesibilidad",
    };

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <Step1
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={projectData.step1}
                    />
                );
            case 2:
                return (
                    <Step2
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={projectData.step2}
                    />
                );
            case 3:
                return (
                    <Step3
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={projectData.step3}
                    />
                );
            case 4:
                return (
                    <Step4
                        onNext={handleNext}
                        onBack={handleBack}
                        initialData={projectData.step4}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="project-flow">
            <StepHeader
                currentStep={currentStep}
                totalSteps={4}
                stepTitle={stepTitles[currentStep]}
            />
            <div className="project-flow__content">{renderStep()}</div>
        </div>
    );
};

export default ProjectFlow;
